import {
  isRichTextDocument as isRichTextDoc,
  renderToHtml,
  type RichTextDocument,
} from "./customRichTextRenderer"

// Legacy type alias for backwards compatibility
type Document = RichTextDocument

/**
 * Accepts a variety of Contentful rich text shapes and returns a Document when possible.
 * - string: tries JSON.parse
 * - Document: returns as-is
 * - { raw: string | Document }: unwraps, then parses if needed
 * - { json: Document }: unwraps
 *
 * Returns null when it cannot produce a valid Document.
 */
export function ensureRichTextDocument(input: unknown): Document | null {
  if (input == null) {
    return null
  }

  // Already a Document?
  if (isRichTextDoc(input)) {
    return input
  }

  // Common Contentful wrappers
  if (typeof input === "object") {
    const obj = input as Record<string, unknown>

    if ("json" in obj && isRichTextDoc(obj.json)) {
      return obj.json as Document
    }

    if ("raw" in obj) {
      const raw = obj.raw as unknown

      if (isRichTextDoc(raw)) {
        return raw as Document
      }

      const parsedFromRaw = safeParse<Document>(raw)
      if (parsedFromRaw && isRichTextDoc(parsedFromRaw)) {
        return parsedFromRaw
      }
    }
  }

  // Raw string JSON
  if (typeof input === "string") {
    const parsed = safeParse<Document>(input)
    if (parsed && isRichTextDoc(parsed)) {
      return parsed
    }
  }

  return null
}

/**
 * Convenience: convert any supported input to HTML.
 * Returns "" if input cannot be converted.
 *
 * Note: options parameter kept for backwards compatibility but not used
 * by the custom renderer's HTML output
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export function richTextToHtml(input: unknown, options?: any): string {
  const doc = ensureRichTextDocument(input)
  if (!doc) {
    return ""
  }
  return renderToHtml(doc)
}

/** Safely JSON.parse a string. Returns undefined on failure. */
function safeParse<T = unknown>(maybeJson: unknown): T | undefined {
  if (typeof maybeJson !== "string") {
    return undefined
  }

  try {
    const parsed = JSON.parse(maybeJson) as T
    return parsed
  } catch {
    return undefined
  }
}
